/**
 * The Navigate panes that are new screens rather than an existing window
 * opened inside Navigate (2026-10-05, app-study.html):
 *
 *   - Timelines: see what is playing and start or stop one without opening
 *     the editor. The launch is the editor's own (timeline/launch.ts).
 *   - MIDI matrix: "the answer first" - how many numbers are in use, on how
 *     many channels, and which are claimed by more than one device. The full
 *     grid stays full screen (DEC-096).
 *   - Jump back in: the last things touched (navigate/recentActivity.ts).
 *
 * Fixed chrome in Navigate's look (DEC-170 values), like the rest of it.
 */

import { followSongTempos, tempoText, tempoTitle } from '../timeline/tempoLabel.ts';
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, untrack, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import { commitTimelineName, copyAsLightingTimeline, createChain, duplicateTimeline, makeFullTimeline, renameTimeline, timelinesInActiveProject } from '../timeline/store.ts';
import { canCopyAsLighting, isLightingTimeline } from '../timeline/lighting.ts';
import { canSetDefaultLights, defaultLightsId, refreshDefaultLights, setDefaultLights } from '../timeline/defaultLights.ts';
import { isChain, chainSteps, waitWords, isWaitStep, firedActionId } from '../timeline/chain.ts';
import { ChainEditor } from './ChainEditor.tsx';
import { followTransport, transportNow } from '../timeline/transport.ts';
import { launchTimelineOnBridge, stopTimelineOnBridge } from '../timeline/launch.ts';
import { bridges, getConnection } from '../opbridge/store.ts';
import { matrixLibrary } from '../matrix/store.ts';
import { activeProjectId, projects } from '../layout/projectStore.ts';
import { recentActivity, type Activity } from '../navigate/recentActivity.ts';
import { pushToast } from '../ui/toast.ts';
import { plainFireReason } from '../opbridge/dispatch.ts';
import { deleteTimelineFromRig } from '../timeline/deleteTimeline.ts';
import { jumpToSectionAction, timelineHomeTarget, timelinePlacement } from '../timeline/bridgeSync.ts';
import { addTimelineLauncher, editItem, type ItemPatch } from '../layout/store.ts';
import { timelineTotalBars, timelineLeadIn, timelineOrigin, type Timeline } from '@opbuttons/schema';
import { sectionHue as kindHue, sectionKindKey } from '../timeline/sectionHue.ts';

function ago(ms: number, now: number = Date.now()): string {
  const m = Math.round(Math.max(0, now - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m === 1 ? 'a minute ago' : `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? 'an hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  return `${Math.round(d / 7)} weeks ago`;
}

/* ─────────────── Timelines ─────────────── */

/** Version B of `public/timelines-study.html` (operator, 2026-10-16: "the timelines menu page needs improving
 *  drastically", then of the first version "it looks a bit cheap tbh", of B "much better!"). One list, hairline rows,
 *  real columns; a thin muted strip of each song's sections under its name; green only for what is playing; a slim
 *  Now playing bar; one click (or tap) anywhere on a row opens it, only Play and "..." do something else; the "..."
 *  and New menus close on a click anywhere else or Esc; Bars, not minutes ("length should be replaced by bars"). */
/** The strip's muted section colours: the shared kind colours (timeline/sectionHue.ts, DEC-281) toned down for a thin
 *  list strip. The usual five keep their hand-picked values; matched by KIND since 2026-10-26 (operator: section
 *  colours "should match"), so VERSE 2 is a muted verse green and GUITAR SOLO a muted solo orange, where an exact-name
 *  lookup had greyed out everything but the five. Only a name of no known kind is grey. */
const SECTION_HUE: Record<string, string> = { intro: '#5f748c', verse: '#667c5d', prechorus: '#8d7a55', chorus: '#8f5d63', bridge: '#6f638f' };
const sectionHue = (name: string): string => {
  const kind = sectionKindKey(name);
  if (!kind) return '#6e7380';
  return SECTION_HUE[kind] ?? `color-mix(in oklab, ${kindHue(name)} 45%, #6e7380)`;
};
/** A section's own colour, when it has one, wins here too (sectionHue.ts), muted the same way. */
const sectionMuted = (sec: { name: string; color?: string | undefined }): string =>
  sec.color && sec.color.trim() ? `color-mix(in oklab, ${sec.color} 45%, #6e7380)` : sectionHue(sec.name);
const sentence = (s: string) => (s && s === s.toUpperCase() ? s.charAt(0) + s.slice(1).toLowerCase() : s);
/** "Your order" first: the project's own order, so a row stays where the hand expects it on stage; an edit or a
 *  duplicate does not move the list around (a Recent default did, found by navigate_timelines_manage, 2026-10-16). */
type TlSort = 'order' | 'recent' | 'az' | 'long';
const SORT_WORD: Record<TlSort, string> = { order: 'Your order', recent: 'Recent', az: 'A to Z', long: 'Longest' };
const NEXT_SORT: Record<TlSort, TlSort> = { order: 'recent', recent: 'az', az: 'long', long: 'order' };
/** Icons as FUNCTIONS: a JSX constant is ONE DOM node in Solid, so shared across rows it moved from row to row, and a
 *  Play press that swapped its icon removed the pressed node mid-click (the row then opened the editor; 2026-10-16). */
const TLI = {
  play: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>,
  stop: () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="6.5" width="11" height="11" rx="1" /></svg>,
  lights: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.2 3.5h7.6l-1.3 6.2H9.5z" /><path d="M10.2 9.7 6 20.5M13.8 9.7l4.2 10.8M12 9.7v10.8" /></svg>,
  guitar: () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="3.5" width="14" height="17" rx="2" /><circle cx="9" cy="8" r="1.6" /><circle cx="15" cy="8" r="1.6" /></svg>,
  controls: () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></svg>,
  auto: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 17.5 9 9l5.5 5 6-8.5" /></svg>,
  search: () => <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5 5" /></svg>,
  add: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>,
};

export function TimelinesPane(props: { onOpen: (id: string) => void; onNew: (tier: 'song' | 'action' | 'lights') => void }): JSX.Element {
  const live = () => bridges().some((b) => getConnection(b.id)?.statusSignal()() === 'connected');
  /** The Song each computer is running, and where: from the timeline transport (2026-10-16; it read its own state
   *  poll, which called a start waiting for its bar line "playing"). Followed while shown; the bar re-read twice a
   *  second while something runs. */
  createEffect(() => {
    const offs = untrack(bridges).map((b) => followTransport(b.id));
    onCleanup(() => offs.forEach((off) => off()));
  });
  const [tick, setTick] = createSignal(0);
  createEffect(() => {
    if (!bridges().some((b) => transportNow(b.id))) return;
    const iv = setInterval(() => setTick((n) => n + 1), 500);
    onCleanup(() => clearInterval(iv));
  });
  const playing = createMemo(() => {
    tick();
    const out = new Map<string, { bar: number; machine: string; waiting: boolean }>();
    for (const b of bridges()) {
      const now = transportNow(b.id);
      if (now) out.set(now.timelineId, { bar: now.bar, machine: b.name, waiting: now.waiting });
    }
    return out;
  });
  const [menu, setMenu] = createSignal<string | null>(null);
  const [newOpen, setNewOpen] = createSignal(false);
  const [renaming, setRenaming] = createSignal<string | null>(null);
  const [deleting, setDeleting] = createSignal<string | null>(null);
  const [query, setQuery] = createSignal('');
  const [sort, setSort] = createSignal<TlSort>('order');
  const nameOf = (id: string) => timelinesInActiveProject().find((t) => t.id === id)?.name ?? 'this timeline';
  /** The Timeline Launchers that launch timeline `id`, on any page of any project, as "project / page: name". */
  const usedBy = (id: string) => projects().flatMap((p) => p.pages.flatMap((pg) => pg.items
    .filter((it) => it.kind === 'timelineLauncher' && (it as { timelineId?: string }).timelineId === id)
    .map((it) => `${p.name} / ${pg.name}${(it as { name?: string }).name ? `: ${(it as { name?: string }).name}` : ''}`)));
  const saveName = (id: string, typed: string) => {
    const before = nameOf(id);
    setRenaming(null);
    if (!typed.trim() || typed.trim() === before) return;
    renameTimeline(id, typed);
    commitTimelineName(id, before);
  };
  const duplicate = (id: string) => {
    const copy = duplicateTimeline(id);
    setMenu(null);
    if (copy) pushToast('ok', `Made "${copy.name}"`, 2000);
  };
  /** Lighting timelines (DEC-323): copy one out of a full timeline, turn one back into a full timeline, make one
   *  performance mode's default lights. */
  const copyAsLighting = (id: string) => {
    setMenu(null);
    const copy = copyAsLightingTimeline(id);
    if (copy) pushToast('ok', `Made "${copy.name}": its lights only, under Lighting`, 2500);
  };
  const makeFull = (id: string) => {
    setMenu(null);
    makeFullTimeline(id);
    pushToast('ok', `"${nameOf(id)}" is a full timeline now, under Songs`, 2500);
  };
  const makeDefault = async (t: Timeline) => {
    setMenu(null);
    const r = await setDefaultLights(t);
    pushToast(r.ok ? 'ok' : 'warn', r.message, r.ok ? 3000 : 5000);
  };
  onMount(() => { void refreshDefaultLights(); });
  const remove = (id: string) => {
    const t = timelinesInActiveProject().find((x) => x.id === id);
    setDeleting(null); setMenu(null);
    if (!t) return;
    if (playing().get(id)) stopTimelineOnBridge(t);
    // From OPBridge too (2026-10-16): this list deleted it on this device
    // only, leaving it on every computer it had been sent to.
    deleteTimelineFromRig(id);
    pushToast('ok', `Deleted "${t.name}"`, 2000);
  };
  /** "Add a launcher to the canvas" (operator's wording, 2026-10-16): a Timeline Launcher for it on the page in view. */
  const addLauncher = (id: string) => {
    setMenu(null);
    const item = addTimelineLauncher();
    if (!item) { pushToast('warn', 'Open a page first: the launcher goes on the page you are on.'); return; }
    editItem(item.id, { timelineId: id } as ItemPatch);
    pushToast('ok', `Added a launcher for "${nameOf(id)}" to this page`, 2500);
  };
  /** A chain opens as its step list here; every other timeline opens in the editor (2026-10-23). */
  const [chainId, setChainId] = createSignal<string | null>(null);
  const open = (t: Timeline) => (isChain(t) ? setChainId(t.id) : props.onOpen(t.id));
  /** A chain's row: its steps in words, in place of a song and bars. */
  const chainWords = (t: Timeline) => {
    const n = chainSteps(t).length;
    if (!n) return 'Chain, empty';
    const first = chainSteps(t).slice(0, 3).map((s) => (isWaitStep(s) ? `wait ${waitWords(s)}` : (s as { label?: string }).label ?? firedActionId(s) ?? '?'));
    return `Chain: ${first.join(', then ')}${n > 3 ? ` (+${n - 3})` : ''}`;
  };
  const play = (t: Timeline) => {
    if (playing().get(t.id)) { stopTimelineOnBridge(t); return; }
    if (!live()) { pushToast('warn', `No computer is connected, so "${t.name}" cannot play. Open it to rehearse on screen.`); return; }
    launchTimelineOnBridge(t);
  };
  // Esc closes the delete sheet, then an open menu. Added on mount, AFTER Navigate's own listener, for the reason in
  // AudioPane. A press anywhere outside an open menu closes it.
  {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deleting()) { e.preventDefault(); e.stopPropagation(); setDeleting(null); return; }
      if (menu() || newOpen()) { e.preventDefault(); e.stopPropagation(); setMenu(null); setNewOpen(false); return; }
      // An open chain goes back to the list first; Navigate's own Esc closes the section after that.
      if (chainId() && !(e.target as Element | null)?.closest('[data-nav-layer]')) { e.preventDefault(); e.stopPropagation(); setChainId(null); }
    };
    const onDown = (e: PointerEvent) => {
      if (!menu() && !newOpen()) return;
      if ((e.target as Element | null)?.closest('.nv-menu, [data-menu-anchor]')) return;
      setMenu(null); setNewOpen(false);
    };
    onMount(() => { window.addEventListener('keydown', onKey, { capture: true }); window.addEventListener('pointerdown', onDown, true); });
    onCleanup(() => { window.removeEventListener('keydown', onKey, { capture: true }); window.removeEventListener('pointerdown', onDown, true); });
  }
  const bars = (t: Timeline) => timelineTotalBars(t);
  // The BPM column (2026-11-03): the timeline's own, its song's in brackets when that differs (tempoLabel.ts).
  followSongTempos(timelinesInActiveProject);
  const modifiedMs = (t: Timeline) => { const ms = Date.parse(t.modified); return Number.isFinite(ms) ? ms : 0; };
  const shown = createMemo(() => {
    const q = query().trim().toLowerCase();
    const all = timelinesInActiveProject().filter((t) => !q || t.name.toLowerCase().includes(q) || (t.testTrack?.name ?? '').toLowerCase().includes(q));
    const by = sort();
    if (by === 'order') return all;
    return [...all].sort((a, b) => (by === 'az' ? a.name.localeCompare(b.name) : by === 'long' ? bars(b) - bars(a) : modifiedMs(b) - modifiedMs(a)));
  });
  // Lighting timelines are their own group, apart from songs (operator 2026-10-27, DEC-323).
  const lighting = () => shown().filter((t) => isLightingTimeline(t));
  const songs = () => shown().filter((t) => t.tier !== 'action' && !isLightingTimeline(t));
  const actions = () => shown().filter((t) => t.tier === 'action' && !isLightingTimeline(t));
  /** The one playing now (the first, when several computers each run one). */
  const nowPlaying = createMemo(() => {
    for (const t of timelinesInActiveProject()) { const n = playing().get(t.id); if (n) return { t, n }; }
    return null;
  });
  const sectionAt = (t: Timeline, bar1: number) => {
    let at = timelineLeadIn(t);
    if (bar1 - 1 < at) return t.sections[0];
    for (const sec of t.sections) { if (bar1 - 1 < at + sec.lengthBars) return sec; at += sec.lengthBars; }
    return t.sections[t.sections.length - 1];
  };
  const lanesOf = (t: Timeline) => {
    let lights = 0; let guitar = 0; let auto = 0; let controls = 0;
    for (const l of t.lanes) {
      const k = l.laneKind ?? 'mixed';
      if (k === 'dmx-rig' || k === 'dmx-fixture') lights++;
      else if (k === 'guitar') guitar++;
      else if (k === 'automation') auto++;
      else if (k !== 'marker') controls++;
    }
    return { lights, guitar, auto, controls };
  };
  const warnOf = (t: Timeline): string | null => {
    if (t.tier === 'genre') return "OPBridge can't play Genre timelines yet";
    if (!timelinePlacement(t).home) return 'No computer to play it on';
    return null;
  };

  const Row = (rp: { t: Timeline }) => {
    const t = rp.t;
    const now = () => playing().get(t.id);
    const home = () => timelinePlacement(t).home?.name ?? null;
    const n = () => usedBy(t.id).length;
    const lanes = () => lanesOf(t);
    return (
      <div class="nv-lib-row" data-testid="tl-row" data-id={t.id} data-playing={now() ? 'true' : undefined}
        role="button" tabindex={0} title={`Open ${t.name}`}
        onClick={(e) => { if ((e.target as Element).closest('button, input, .nv-menu')) return; open(t); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(t); }}>
        <button type="button" class="nv-lib-play" data-testid="tl-play" disabled={t.tier === 'genre'}
          aria-label={`${now() ? 'Stop' : 'Play'} ${t.name}`} title={now() ? 'Stop' : `Play${home() ? ` on ${home()}` : ''}`}
          onClick={(e) => { e.stopPropagation(); play(t); }}>{now() ? TLI.stop() : TLI.play()}</button>
        <div class="nav-lr-main nv-lib-name">
          <Show when={renaming() === t.id} fallback={<b data-testid="tl-open">{t.name}</b>}>
            {/* Saved on Enter or leaving the box, not per keystroke (a save redraws this row). `data-nav-layer`
                makes Navigate's Esc wait, so Esc here only cancels the name (navigate_timelines_manage step 3). */}
            <span class="nv-fld" data-nav-layer><input data-testid="tl-rename-in" value={t.name} aria-label="Timeline name"
              ref={(el) => queueMicrotask(() => { el.focus(); el.select(); })}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); return; }
                if (e.key === 'Enter') saveName(t.id, e.currentTarget.value);
              }}
              onBlur={(e) => { if (renaming() === t.id) saveName(t.id, e.currentTarget.value); }} /></span>
          </Show>
          <img class="nv-origin" data-testid="tl-origin" data-origin={timelineOrigin(t)} src={`/logo/${timelineOrigin(t)}-logo.png`}
            alt={`Made in ${timelineOrigin(t) === 'opbridge' ? 'OPBridge' : 'OPButtons'}`} title={`Made in ${timelineOrigin(t) === 'opbridge' ? 'OPBridge' : 'OPButtons'}`} />
          <Show when={t.tier !== 'action' && t.sections.length > 0}>
            <span class="nv-strip" aria-hidden="true">
              <For each={t.sections}>{(sec) => <i style={{ 'flex-grow': String(sec.lengthBars), background: sectionMuted(sec) }} title={`${sentence(sec.name)}, ${sec.lengthBars} bars`} />}</For>
            </span>
          </Show>
          <small>
            {now() ? (now()!.waiting ? `Starting at the next bar line on ${now()!.machine}` : `Playing, bar ${Math.min(now()!.bar, bars(t))} on ${now()!.machine}`) : ''}
          </small>
          <span class="nv-lib-sub">{[t.testTrack?.name, defaultLightsId() === t.id ? 'Default lights' : null, home() ? `${home()}${n() ? `, ${n()} launcher${n() === 1 ? '' : 's'}` : ''}` : null].filter(Boolean).join(' \u00b7 ')}</span>
          <Show when={warnOf(t)}><span class="nv-lib-warn">{warnOf(t)}</span></Show>
        </div>
        <span class="nv-lib-c nv-lib-wide" data-dim={t.testTrack || isChain(t) || defaultLightsId() === t.id ? undefined : 'true'} data-testid={isChain(t) ? 'tl-chain-words' : undefined}>{isChain(t) ? chainWords(t) : isLightingTimeline(t) ? (defaultLightsId() === t.id ? 'Default lights' : '') : t.testTrack?.name ?? (t.tier === 'action' ? '' : 'No song')}</span>
        <span class="nv-lib-c nv-lib-num" title={isChain(t) ? `${chainSteps(t).length} steps` : `${bars(t)} bar${bars(t) === 1 ? '' : 's'}`}>{isChain(t) ? '' : bars(t)}</span>
        <span class="nv-lib-c nv-lib-bpm" data-testid="tl-bpm" title={isChain(t) ? '' : tempoTitle(t)}>{isChain(t) ? '' : tempoText(t)}</span>
        <span class="nv-lib-moves">
          <Show when={lanes().lights}><span title="Lights lanes">{TLI.lights()}{lanes().lights}</span></Show>
          <Show when={lanes().guitar}><span title="Guitar lanes">{TLI.guitar()}{lanes().guitar}</span></Show>
          <Show when={lanes().controls}><span title="Controls lanes">{TLI.controls()}{lanes().controls}</span></Show>
          <Show when={lanes().auto}><span title="Automation lanes">{TLI.auto()}{lanes().auto}</span></Show>
        </span>
        <span class="nv-lib-c nv-lib-wide" data-dim={home() ? undefined : 'true'}>{home() ?? 'Not set'}<Show when={n()}><small>{n()} launcher{n() === 1 ? '' : 's'}</small></Show></span>
        <span class="nv-lib-c nv-lib-wide" data-dim="true">{modifiedMs(t) ? ago(modifiedMs(t)) : ''}</span>
        <span class="nv-menu-wrap">
          <button type="button" class="nv-lib-dots" data-menu-anchor data-testid="tl-more" aria-label={`More for ${t.name}`} aria-expanded={menu() === t.id}
            onClick={(e) => { e.stopPropagation(); setMenu(menu() === t.id ? null : t.id); }}>{'\u00b7\u00b7\u00b7'}</button>
          <Show when={menu() === t.id}>
            <div class="nv-menu" role="menu" data-nav-layer data-testid="tl-tools">
              <button type="button" role="menuitem" data-testid="tl-rename" onClick={() => { setMenu(null); setRenaming(t.id); }}>Rename</button>
              <button type="button" role="menuitem" data-testid="tl-duplicate" onClick={() => duplicate(t.id)}>Duplicate</button>
              <Show when={canCopyAsLighting(t)}>
                <button type="button" role="menuitem" data-testid="tl-copy-lighting" onClick={() => copyAsLighting(t.id)}>Copy as lighting timeline</button>
              </Show>
              <Show when={isLightingTimeline(t) && canSetDefaultLights()}>
                <button type="button" role="menuitem" data-testid="tl-set-default-lights" disabled={defaultLightsId() === t.id} onClick={() => void makeDefault(t)}>
                  {defaultLightsId() === t.id ? 'The default lights' : 'Set as default lights'}
                </button>
              </Show>
              <Show when={isLightingTimeline(t)}>
                <button type="button" role="menuitem" data-testid="tl-make-full" onClick={() => makeFull(t.id)}>Make it a full timeline</button>
              </Show>
              <button type="button" role="menuitem" data-testid="tl-launcher" onClick={() => addLauncher(t.id)}>Add a launcher to the canvas</button>
              <button type="button" role="menuitem" data-danger data-testid="tl-delete" onClick={() => { setMenu(null); setDeleting(t.id); }}>Delete</button>
            </div>
          </Show>
        </span>
      </div>
    );
  };

  return (
    <div class="nav-pane-body nav-timelines" role="region" aria-label="Timelines" data-testid="nav-timelines">
      <Show when={chainId() && timelinesInActiveProject().some((x) => x.id === chainId())}>
        <ChainEditor id={chainId()!} onBack={() => setChainId(null)} onPlay={play} playing={!!playing().get(chainId()!)} />
      </Show>
      <Show when={!chainId() || !timelinesInActiveProject().some((x) => x.id === chainId())}>
      <div class="nv-row nv-lib-tools">
        <label class="nv-lib-search">{TLI.search()}<input data-testid="tl-search" placeholder="Search" aria-label="Search timelines and songs" value={query()} onInput={(e) => setQuery(e.currentTarget.value)} /></label>
        <button type="button" class="nv-b nv-b-q nv-b-sm" data-testid="tl-sort" onClick={() => setSort(NEXT_SORT[sort()])}>Sort <b>{SORT_WORD[sort()]}</b></button>
        <span class="nv-menu-wrap">
          <button type="button" class="nv-b nv-b-sm" data-menu-anchor data-testid="tl-new" aria-expanded={newOpen()} onClick={() => setNewOpen(!newOpen())}>{TLI.add()}New</button>
          <Show when={newOpen()}>
            <div class="nv-menu nv-menu-wide" role="menu" data-nav-layer>
              <button type="button" role="menuitem" data-testid="tl-new-song" onClick={() => { setNewOpen(false); props.onNew('song'); }}><b>Song timeline</b><small>A whole song in sections, started from a launcher or the setlist.</small></button>
              <button type="button" role="menuitem" data-testid="tl-new-lighting" onClick={() => { setNewOpen(false); props.onNew('lights'); }}><b>Lighting timeline</b><small>Your lights only: a look to run on its own, or as the default lights between songs.</small></button>
              <button type="button" role="menuitem" data-testid="tl-new-action" onClick={() => { setNewOpen(false); props.onNew('action'); }}><b>Action timeline</b><small>A short burst (a drop, a blackout) fired from one button.</small></button>
              <button type="button" role="menuitem" data-testid="tl-new-chain" onClick={() => { setNewOpen(false); setChainId(createChain('New chain').id); }}><b>Chain</b><small>A few actions one after another, with waits between: column 3, wait 300 ms, column 4.</small></button>
            </div>
          </Show>
        </span>
      </div>

      <Show when={nowPlaying()}>
        {(np) => {
          const sec = () => sectionAt(np().t, np().n.bar);
          const total = () => bars(np().t);
          return (
            <div class="nv-now" data-testid="tl-now">
              <span class="nv-now-l">{np().n.waiting ? 'Starting' : 'Now playing'}</span>
              <span class="nv-now-nm"><b>{np().t.name}</b>
                <span>{np().n.waiting ? `at the next bar line on ${np().n.machine}` : `${sentence(sec()?.name ?? '')} \u00b7 bar ${Math.min(np().n.bar, total())} of ${total()} \u00b7 ${np().n.machine}`}</span></span>
              <span class="nv-now-ctl">
                <button type="button" class="nv-b nv-b-sm" onClick={() => props.onOpen(np().t.id)}>Open</button>
                <button type="button" class="nv-b nv-b-sm" data-testid="tl-now-stop" onClick={() => stopTimelineOnBridge(np().t)}>Stop</button>
              </span>
              <span class="nv-now-prog">
                <For each={np().t.sections}>{(s2) => <i style={{ 'flex-grow': String(s2.lengthBars) }} />}</For>
                <em style={{ width: `${Math.max(0, Math.min(100, ((np().n.bar - 1) / Math.max(1, total())) * 100))}%` }} />
              </span>
              <Show when={np().t.tier === 'song' && np().t.sections.length > 1}>
                <span class="nv-now-jump">
                  <For each={np().t.sections}>{(s2) => (
                    <button type="button" data-on={sec()?.id === s2.id ? 'true' : undefined}
                      title={`Jump to ${sentence(s2.name)} at the next bar line`}
                      onClick={() => {
                        // Said when it fails, as every other jump is (2026-11-04 timeline audit, OPButtons 5: it was silent).
                        void jumpToSectionAction(s2.id, timelineHomeTarget(np().t)).then((r) => {
                          const failed = r.filter((x) => x.kind !== 'ok');
                          if (failed.length > 0) pushToast('error', `The jump to ${sentence(s2.name)} did not happen: ${plainFireReason(failed[0]!)}.`);
                        });
                      }}>{sentence(s2.name)}</button>
                  )}</For>
                </span>
              </Show>
            </div>
          );
        }}
      </Show>

      <Show
        when={timelinesInActiveProject().length > 0}
        fallback={<p class="nav-empty">No timelines in this project yet. "New" makes one.</p>}
      >
        <div class="nv-lib">
          <div class="nv-lib-cols" aria-hidden="true"><span /><span>Name</span><span class="nv-lib-wide">Song</span><span class="nv-lib-num">Bars</span><span class="nv-lib-bpm">BPM</span><span>Moves</span><span class="nv-lib-wide">Plays on</span><span class="nv-lib-wide">Edited</span><span /></div>
          <Show when={songs().length}><div class="nv-lib-grp"><b>Songs</b>{songs().length}</div><For each={songs()}>{(t) => <Row t={t} />}</For></Show>
          <Show when={lighting().length}><div class="nv-lib-grp" data-testid="tl-group-lighting"><b>Lighting</b>{lighting().length}</div><For each={lighting()}>{(t) => <Row t={t} />}</For></Show>
          <Show when={actions().length}><div class="nv-lib-grp"><b>Actions</b>{actions().length}</div><For each={actions()}>{(t) => <Row t={t} />}</For></Show>
          <Show when={shown().length === 0}><p class="nav-empty">Nothing matches "{query()}".</p></Show>
        </div>
      </Show>

      </Show>

      <Show when={deleting()}>
        {(id) => (<Portal mount={shellPortal() ?? document.body}>
          <div class="nv-sheet-scrim" role="dialog" aria-modal="true" aria-label="Delete timeline" data-testid="tl-delete-sheet">
            <div class="nv-sheet">
              <div class="nv-sheet-h"><h2>Delete {nameOf(id())}?</h2><p>Its sections and every lane go with it. Undo in the timeline editor brings it back.</p></div>
              <div class="nv-sheet-b">
                <Show when={usedBy(id()).length} fallback={<p class="nv-note">No launcher on the canvas starts it.</p>}>
                  <p class="nv-note" data-tone="bad" data-testid="tl-in-use">Started by {usedBy(id()).length === 1 ? 'a launcher' : `${usedBy(id()).length} launchers`} on the canvas: {usedBy(id()).join(', ')}. {usedBy(id()).length === 1 ? 'It' : 'They'} will start nothing until you choose another timeline.</p>
                </Show>
              </div>
              <div class="nv-sheet-f"><div class="nv-row">
                <span class="nv-sp" />
                <button type="button" class="nv-b" onClick={() => setDeleting(null)}>Cancel</button>
                <button type="button" class="nv-b nv-b-pri" data-testid="tl-delete-yes" onClick={() => remove(id())}>Delete</button>
              </div></div>
            </div>
          </div>
        </Portal>)}
      </Show>
    </div>
  );
}

/* ─────────────── MIDI matrix ─────────────── */

export function MatrixPane(): JSX.Element {
  const stats = createMemo(() => {
    const lib = matrixLibrary();
    const used = lib.slots.filter((s) => s.claims.length > 0);
    const channels = new Set(used.map((s) => s.channel));
    const shared = used.filter((s) => new Set(s.claims.map((c) => c.deviceId)).size > 1);
    const perDevice = lib.devices.map((d) => ({
      id: d.id,
      name: d.name,
      n: used.filter((s) => s.claims.some((c) => c.deviceId === d.id)).length,
    }));
    return { used: used.length, channels: channels.size, shared: shared.length, perDevice };
  });
  return (
    <div class="nav-pane-body nav-matrix" role="region" aria-label="MIDI matrix">
      <div class="logo-menu-status nav-matrix-stats">
        <div class="logo-menu-st-card">
          <span class="logo-menu-st-head">In use</span>
          <span class="logo-menu-st-value">{stats().used} {stats().used === 1 ? 'number' : 'numbers'}</span>
          <span class="logo-menu-st-meta">On {stats().channels} {stats().channels === 1 ? 'channel' : 'channels'}.</span>
        </div>
        <div class="logo-menu-st-card" data-status={stats().shared > 0 ? 'warn' : 'pass'}>
          <span class="logo-menu-st-head">Shared</span>
          <span class="logo-menu-st-value">{stats().shared === 0 ? 'No clashes' : `${stats().shared} shared`}</span>
          <span class="logo-menu-st-meta">
            {stats().shared === 0 ? 'No number is claimed by two devices.' : 'Numbers claimed by more than one device. The full matrix shows which.'}
          </span>
        </div>
        <div class="logo-menu-st-card">
          <span class="logo-menu-st-head">Devices</span>
          <span class="logo-menu-st-value">{stats().perDevice.length}</span>
          <span class="logo-menu-st-meta">Each one's share is listed below.</span>
        </div>
      </div>
      <Show when={stats().perDevice.length > 0}>
        <div class="nav-well">
          <For each={stats().perDevice}>
            {(d) => (
              <div class="nav-lr">
                <span class="nav-lr-main"><b>{d.name}</b></span>
                <span class="nav-lr-trail"><small>{d.n} {d.n === 1 ? 'number' : 'numbers'}</small></span>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

/* ─────────────── Jump back in ─────────────── */

export function JumpBackIn(props: { onPick: (a: Activity) => void }): JSX.Element {
  // This project's things, plus the rig-wide ones (lights).
  const rows = createMemo(() => recentActivity().filter((a) => a.kind === 'lights' || !a.projectId || a.projectId === activeProjectId()).slice(0, 3));
  const sub = (a: Activity): string =>
    a.kind === 'timeline' ? `Timeline, edited ${ago(a.at)}`
      : a.kind === 'control' ? `Control on ${a.pageName ?? 'a page'}, edited ${ago(a.at)}`
      : `Opened ${ago(a.at)}`;
  return (
    <Show when={rows().length > 0} fallback={<p class="nav-empty">What you edit shows up here, one tap away.</p>}>
      <div class="nav-well">
        <For each={rows()}>
          {(a) => (
            <button type="button" class="nav-lr nav-lr-button" data-kind={a.kind} onClick={() => props.onPick(a)}>
              <span class="nav-lr-main"><b>{a.title}</b><small>{sub(a)}</small></span>
              <span class="nav-lr-chev" aria-hidden="true">›</span>
            </button>
          )}
        </For>
      </div>
    </Show>
  );
}
